import React, { memo, useCallback } from 'react';
import { SkeletonLoading } from '@saleshandy/design-system';
import { map } from 'lodash';
import FilterTab from '../../../../../../../shared/design-system/components/atoms/filter-tab';
import HorizontalMenu from '../../../../../../../shared/design-system/components/atoms/horizontal-menu';
import { filterToProperties } from '../../../../../helpers/prospect-filters-resolver';
import { Filters } from '../../../../../../../shared/types/prospects-filters';
import { IProps } from './types';
import { constants } from '../../../../../../../shared/enums/constants';
import { emailFilterToProperties } from '../../../../../../sequence/components/sequence-single-content/components/sequence-single-emails/helper/sequence-emails-filters-resolver';

/**
 * Renders all the applied Filters.
 * If the applied filters exceeds the view port then they are horizontally scrollable.
 */
const ProspectsFilterBar: React.FC<IProps> = ({
  prospectsFilters,
  resetProspectsFilters,
  getProspectsCount,
  getProspects,
  handleClearSelection,
  isLoading,
  isSequenceEmailsFilter = false,
  search,
  appliedFiltersCount,
  tooltipOptions,
}) => {
  let isEmpty = true;
  const filterToPropertiesTemp = isSequenceEmailsFilter
    ? emailFilterToProperties
    : filterToProperties;
  Object.keys(prospectsFilters)?.forEach((obj) => {
    if (prospectsFilters[obj]?.length > 0) {
      isEmpty = false;
    }
  });

  const onCancel = useCallback(
    (filter: Filters) => {
      resetProspectsFilters(filter);
      getProspects?.({
        pageNum: 0,
        pageSize: constants.PROSPECT_PAGE_SIZE,
        search,
      });
      getProspectsCount?.({ search });
      handleClearSelection && handleClearSelection();
    },
    [resetProspectsFilters, getProspectsCount, getProspects],
  );

  if (isEmpty) {
    return null;
  }

  if (isLoading) {
    const widthForLoaders = [160, 130, 170, 140, 120, 150];
    return (
      <div className="d-flex prospect-filter-bar-loader">
        {Array.from({ length: appliedFiltersCount || 4 }).map((_, index) => (
          <SkeletonLoading
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            width={widthForLoaders[index] || 100}
            height={30.6}
          />
        ))}
      </div>
    );
  }

  return (
    <HorizontalMenu>
      {Object.keys(prospectsFilters)?.map((key, idx) => (
        <FilterTab
          filters={
            key === Filters.SubSequenceStepIds
              ? prospectsFilters[key]
              : map(
                  key === Filters.Status
                    ? prospectsFilters[key]?.filter(
                        (item) => !item?.isHeaderFilter,
                      )
                    : prospectsFilters[key],
                  filterToPropertiesTemp[key]?.displayNameKey,
                )
          }
          name={
            isSequenceEmailsFilter
              ? emailFilterToProperties[key]?.displayLabel
              : key
          }
          key={prospectsFilters[key]}
          itemId={idx}
          onCancel={() => onCancel(key as Filters)}
          separator={filterToPropertiesTemp[key]?.separator}
          tooltipOptions={tooltipOptions}
        />
      ))}
    </HorizontalMenu>
  );
};

export default memo(ProspectsFilterBar);
